import React from 'react'
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { t } from 'ttag'
import { consistencyUnit, dailyGoalUnit, FormData, GoalType } from '../../ChallengeAdmin'
import { CustomTypeCategory, customTypes, customUnits } from '../../../../services/CustomTypes'
import { isSimpleDayType } from '../../../../common/helpers/challenges'
import YumAccordion from '../../../../components/YumAccordion/YumAccordion'
import YumAccordionSummary from '../../../../components/YumAccordionSummary/YumAccordionSummary'
import Typography from '@material-ui/core/Typography'
import { isRtl } from '../../../../i18nInit'
import { AccordionDetails, FormGroup, InputAdornment, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { red } from '../../../../styles/material-ui-theme-v2'
import YumSelect from '../../../../components/YumSelect/YumSelect'
import { unitsCountDurationMinuteLong, unitsCountEnergyKilocalorieShort } from '../../../../common/helpers/units'
import YumField from '../../../../components/YumInput/YumInput'
import YumFormSwitch from '../../../../components/YumFormSwitch/YumFormSwitch'

import { ReactComponent as TotalTargetActiveIcon } from '../../../../assets/v3/icons/total-target-active.svg'
import { ReactComponent as TotalTargetInactiveIcon } from '../../../../assets/v3/icons/total-target-inactive.svg'

import { ReactComponent as ConsistencyTargetActiveIcon } from '../../../../assets/v3/icons/consistency-target-active.svg'
import { ReactComponent as ConsistencyTargetInactiveIcon } from '../../../../assets/v3/icons/consistency-target-inactive.svg'

import { ReactComponent as SimpleManualEntryActiveIcon } from '../../../../assets/v3/icons/simple-manual-entry-active.svg'
import { ReactComponent as SimpleManualEntryInactiveIcon } from '../../../../assets/v3/icons/simple-manual-entry-inactive.svg'

import { ReactComponent as CollectiveTargetActiveIcon } from '../../../../assets/v3/icons/collective-target-active.svg'
import { ReactComponent as CollectiveTargetInactiveIcon } from '../../../../assets/v3/icons/collective-target-inactive.svg'

import { ReactComponent as DailyGoalActiveIcon } from '../../../../assets/v3/icons/daily-goal-active.svg'
import { ReactComponent as DailyGoalInactiveIcon } from '../../../../assets/v3/icons/daily-goal-inactive.svg'

import { ReactComponent as TotalAmountActiveIcon } from '../../../../assets/v3/icons/total-amount-active.svg'
import { ReactComponent as TotalAmountInactiveIcon } from '../../../../assets/v3/icons/total-amount-inactive.svg'

import { ReactComponent as UnitActiveIcon } from '../../../../assets/v3/icons/unit-active.svg'
import { ReactComponent as UnitInactiveIcon } from '../../../../assets/v3/icons/unit-inactive.svg'

import { ReactComponent as CollectiveGoalActiveIcon } from '../../../../assets/v3/icons/collective-goal-active.svg'
import { ReactComponent as CollectiveGoalInactiveIcon } from '../../../../assets/v3/icons/collective-goal-inactive.svg'

import { ReactComponent as DateActiveIcon } from '../../../../assets/v3/icons/date-active.svg'
import { ReactComponent as DateInactiveIcon } from '../../../../assets/v3/icons/date-inactive.svg'

interface ChallengeStepPeriodProps {
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  formStep: number
}

export default function ChallengeStepGoal(props: ChallengeStepPeriodProps) {
  const { control, watch, setValue, errors, activityType, formStep } = props

  const goalType = watch('goalType')
  const customUnit = watch('customUnit')
  const customType = watch('customType')
  const manualEntryEnabled = watch('manualEntryEnabled')
  const simpleManualEntry = watch('simpleManualEntry')
  const days = watch('days')

  const handleChange = (newGoalType: GoalType) => (event: any, newExpanded: boolean) => {
    setValue('goalType', newExpanded ? newGoalType : '')
  }

  const customUnitField = customUnits.filter((unit) => unit.category === activityType)?.length > 1 && (
    <Controller
      name="customType"
      rules={{
        min: formStep === 4 && activityType === CustomTypeCategory.ALL ? 1 : undefined,
        max: formStep === 4 && activityType === CustomTypeCategory.ALL ? 1000 : undefined,
        required: formStep === 4 && activityType === CustomTypeCategory.ALL,
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        // @ts-ignore
        <YumSelect
          label={t`Unit`}
          startIcon={UnitActiveIcon}
          {...otherField}
          style={{ background: 'white', marginBottom: '12px' }}
        >
          {customUnits.map(
            (unit, keyIndex) =>
              unit.category === activityType && (
                <option key={keyIndex} value={unit.id}>
                  {unit.label}
                </option>
              ),
          )}
        </YumSelect>
      )}
    />
  )

  const customUnitFieldWithText = customUnits.find((cu) => cu.id === customType)?.text

  const dailyGoalUnitWithField = (
    <>
      {activityType === CustomTypeCategory.STEPS
        ? t`steps`
        : activityType === CustomTypeCategory.INTENSIVE_MINUTES
        ? unitsCountDurationMinuteLong
        : activityType === CustomTypeCategory.CALORIES
        ? unitsCountEnergyKilocalorieShort
        : activityType === CustomTypeCategory.ALL
        ? '%'
        : customUnitFieldWithText}
    </>
  )

  // TODO: Remove this
  const isDefinedCustomActivity = customTypes.some((ct) => ct.id === activityType)

  const dailyGoalField = (
    <Controller
      name="dailyGoal"
      rules={{
        min:
          formStep === 4 &&
          ((!isDefinedCustomActivity &&
            activityType !== CustomTypeCategory.CUSTOM &&
            activityType !== CustomTypeCategory.ALL &&
            goalType === 'totalAmount') ||
            goalType === 'consistentDays')
            ? 1
            : undefined,
        max:
          formStep === 4 &&
          ((!isDefinedCustomActivity &&
            activityType !== CustomTypeCategory.CUSTOM &&
            activityType !== CustomTypeCategory.ALL &&
            goalType === 'totalAmount') ||
            goalType === 'consistentDays')
            ? 100000
            : undefined,
        required:
          formStep === 4 &&
          ((!isDefinedCustomActivity &&
            activityType !== CustomTypeCategory.CUSTOM &&
            activityType !== CustomTypeCategory.ALL &&
            goalType === 'totalAmount') ||
            goalType === 'consistentDays'),
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Daily goal`}
          type="number"
          inputProps={{ inputMode: 'numeric' }}
          onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
          error={!!errors.dailyGoal}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ flexShrink: 0, pointerEvents: 'none' }} position="end" disableTypography>
                {activityType === CustomTypeCategory.CUSTOM ? customUnit : dailyGoalUnitWithField}
              </InputAdornment>
            ),
            style: {
              background: 'white',
            },
          }}
          startIcon={DailyGoalActiveIcon}
          startIconInactive={DailyGoalInactiveIcon}
          {...otherField}
        />
      )}
    />
  )

  const customUnitInput = (
    <Controller
      name="customUnit"
      rules={{
        required:
          formStep === 4 &&
          activityType === CustomTypeCategory.CUSTOM &&
          !simpleManualEntry &&
          ['totalAmount', 'consistentDays', 'collective'].includes(goalType),
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Unit`}
          type="text"
          error={!!errors.customUnit}
          inputProps={{
            autoCapitalize: 'none',
            autoComplete: 'off',
          }}
          InputProps={{
            style: {
              background: 'white',
            },
          }}
          startIcon={UnitActiveIcon}
          startIconInactive={UnitInactiveIcon}
          {...otherField}
        />
      )}
    />
  )

  const totalGoalField = (
    <Controller
      name="totalGoal"
      rules={{
        min:
          formStep === 4 &&
          (activityType === CustomTypeCategory.CUSTOM || isDefinedCustomActivity) &&
          goalType === 'totalAmount'
            ? 1
            : undefined,
        max: formStep === 4 && goalType === 'totalAmount' ? 2000000000 : undefined,
        required:
          formStep === 4 &&
          (activityType === CustomTypeCategory.CUSTOM || isDefinedCustomActivity) &&
          goalType === 'totalAmount',
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Total goal`}
          type="number"
          inputProps={{ inputMode: 'numeric' }}
          onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
          error={!!errors.totalGoal}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ flexShrink: 0, pointerEvents: 'none' }} position="end" disableTypography>
                {activityType === CustomTypeCategory.CUSTOM ? customUnit : customUnitFieldWithText}
              </InputAdornment>
            ),
            style: {
              background: 'white',
            },
          }}
          startIcon={TotalAmountActiveIcon}
          startIconInactive={TotalAmountInactiveIcon}
          style={{
            marginBottom: 0,
          }}
          {...otherField}
        />
      )}
    />
  )

  const customTotalAmountInputs = (
    <>
      {activityType !== CustomTypeCategory.POINTS && (isDefinedCustomActivity ? customUnitField : customUnitInput)}
      {totalGoalField}
    </>
  )
  const indexTotalAmountInputs = (
    <Controller
      name="indexTarget"
      rules={{
        min: formStep === 4 && activityType === CustomTypeCategory.ALL ? 1 : undefined,
        max: formStep === 4 && activityType === CustomTypeCategory.ALL ? 1000 : undefined,
        required: formStep === 4 && activityType === CustomTypeCategory.ALL,
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Target`}
          type="number"
          inputProps={{ inputMode: 'numeric' }}
          onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
          error={!!errors.indexTarget}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ flexShrink: 0, pointerEvents: 'none' }} position="end" disableTypography>
                {dailyGoalUnitWithField}
              </InputAdornment>
            ),
            style: {
              background: 'white',
            },
          }}
          startIcon={TotalAmountActiveIcon}
          startIconInactive={TotalAmountInactiveIcon}
          {...otherField}
        />
      )}
    />
  )

  const consistencyGoalField = (
    <Controller
      name="persistenceGoal"
      rules={{
        min: formStep === 4 && ['consistentDays'].includes(goalType) ? 1 : undefined,
        max: formStep === 4 && ['consistentDays'].includes(goalType) ? days : undefined,
        required: formStep === 4 && ['consistentDays'].includes(goalType),
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Consistency goal`}
          type="number"
          inputProps={{ inputMode: 'numeric' }}
          onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
          error={!!errors.persistenceGoal}
          startIcon={DateActiveIcon}
          startIconInactive={DateInactiveIcon}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ flexShrink: 0, pointerEvents: 'none' }} position="end" disableTypography>
                {consistencyUnit(days)}
              </InputAdornment>
            ),
            style: {
              background: 'white',
            },
          }}
          style={{
            marginBottom: 0,
          }}
          {...otherField}
        />
      )}
    />
  )

  const simpleManualEntryField = (
    <FormGroup>
      <ListItem
        style={{ backgroundColor: 'white' }}
        role={undefined}
        dense
        button
        onClick={() => setValue('simpleManualEntry', !simpleManualEntry)}
      >
        <ListItemIcon>
          {simpleManualEntry ? <SimpleManualEntryActiveIcon /> : <SimpleManualEntryInactiveIcon />}
        </ListItemIcon>
        <ListItemText primary={t`One-click manual entry`} />
        <ListItemIcon>
          <YumFormSwitch control={control} name="simpleManualEntry" />
        </ListItemIcon>
      </ListItem>
    </FormGroup>
  )

  const consistencyGoalInputs = (
    <>
      {manualEntryEnabled &&
        !isSimpleDayType(activityType as CustomTypeCategory) &&
        activityType !== CustomTypeCategory.ALL &&
        simpleManualEntryField}
      {activityType === CustomTypeCategory.CUSTOM &&
        (!simpleManualEntry || activityType !== CustomTypeCategory.CUSTOM) &&
        customUnitInput}
      {isDefinedCustomActivity && activityType !== CustomTypeCategory.POINTS && customUnitField}
      {activityType === CustomTypeCategory.ALL && indexTotalAmountInputs}
      {activityType !== CustomTypeCategory.ALL &&
        !isSimpleDayType(activityType as CustomTypeCategory) &&
        (!simpleManualEntry || activityType !== CustomTypeCategory.CUSTOM) &&
        dailyGoalField}
      {consistencyGoalField}
    </>
  )

  const collectiveGoalField = (
    <Controller
      name="collectiveGoal"
      rules={{
        min: formStep === 4 && goalType === 'collective' /*data.asCollectiveUnit*/ ? 1 : 0,
        max: formStep === 4 && goalType === 'collective' ? 2000000000 : undefined,
        required: formStep === 4 && goalType === 'collective' /*data.asCollectiveUnit*/,
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Collective goal`}
          type="number"
          inputProps={{ inputMode: 'numeric' }}
          onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
          error={!!errors.collectiveGoal}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ flexShrink: 0, pointerEvents: 'none' }} position="end" disableTypography>
                {isDefinedCustomActivity
                  ? dailyGoalUnitWithField
                  : activityType === CustomTypeCategory.CUSTOM
                  ? customUnit
                  : dailyGoalUnit(activityType, customUnit)}
              </InputAdornment>
            ),
            style: {
              background: 'white',
            },
          }}
          style={{
            marginBottom: 0,
          }}
          startIcon={CollectiveGoalActiveIcon}
          startIconInactive={CollectiveGoalInactiveIcon}
          {...otherField}
        />
      )}
    />
  )

  return (
    <>
      {activityType !== CustomTypeCategory.CALORIES && (
        <>
          {!isSimpleDayType(activityType as CustomTypeCategory) && (
            <YumAccordion expanded={goalType === 'totalAmount'} onChange={handleChange('totalAmount')}>
              <YumAccordionSummary>
                {goalType === 'totalAmount' ? <TotalTargetActiveIcon /> : <TotalTargetInactiveIcon />}
                <Typography
                  variant="body2"
                  style={isRtl ? { marginRight: '10px' } : { marginLeft: '10px' }}
                >{t`Total amount target`}</Typography>
              </YumAccordionSummary>
              <AccordionDetails
                style={
                  activityType === CustomTypeCategory.ALL
                    ? {
                        paddingBottom: '0px',
                      }
                    : {}
                }
              >
                <div
                  style={{
                    flexBasis: '100%',
                  }}
                >
                  {activityType !== CustomTypeCategory.ALL && customTotalAmountInputs}
                  {activityType === CustomTypeCategory.ALL && indexTotalAmountInputs}
                </div>
              </AccordionDetails>
            </YumAccordion>
          )}
          <YumAccordion expanded={goalType === 'consistentDays'} onChange={handleChange('consistentDays')}>
            <YumAccordionSummary>
              {goalType === 'consistentDays' ? <ConsistencyTargetActiveIcon /> : <ConsistencyTargetInactiveIcon />}
              <Typography variant="body2" style={isRtl ? { marginRight: '10px' } : { marginLeft: '10px' }}>
                {t`Consistency target`}
              </Typography>
            </YumAccordionSummary>
            <AccordionDetails>
              <div style={{ flexBasis: '100%' }}>{consistencyGoalInputs}</div>
            </AccordionDetails>
          </YumAccordion>
        </>
      )}
      {activityType !== CustomTypeCategory.ALL && (
        <YumAccordion expanded={goalType === 'collective'} onChange={handleChange('collective')}>
          <YumAccordionSummary>
            {goalType === 'collective' ? <CollectiveTargetActiveIcon /> : <CollectiveTargetInactiveIcon />}
            <Typography variant="body2" style={isRtl ? { marginRight: '10px' } : { marginLeft: '10px' }}>
              {t`Collective target`}
            </Typography>
          </YumAccordionSummary>
          <AccordionDetails>
            <div style={{ flexBasis: '100%' }}>
              {isDefinedCustomActivity && customUnitField}
              {activityType === CustomTypeCategory.CUSTOM && !isDefinedCustomActivity ? customUnitInput : ''}
              {collectiveGoalField}
            </div>
          </AccordionDetails>
        </YumAccordion>
      )}
      {!!errors.goalType && !goalType ? (
        <div style={{ marginTop: 20, textAlign: 'center', color: red }}>{t`Please choose a goal.`}</div>
      ) : (
        ''
      )}
    </>
  )
}
